import { Link } from '@tanstack/react-router'
import type { AdminGetPrescreenByIdResponse } from '@workspace/api-backend'
import { Badge } from '@workspace/ui/components/badge'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@workspace/ui/components/collapsible'
import { cn } from '@workspace/ui/lib/utils'
import { ChevronDown } from 'lucide-react'
import type * as React from 'react'
import { useState } from 'react'
import { MarkdownContent } from '@/components/markdown-content'

type Prescreen = AdminGetPrescreenByIdResponse['data']
type PrescreenEvaluation = NonNullable<Prescreen['evaluation']>
type IndividualMetricEvaluation =
  PrescreenEvaluation['prescreenIndividualMetricEvaluations'][number]
type SignalStrength = NonNullable<PrescreenEvaluation['signalStrength']>
type BadgeVariant = NonNullable<React.ComponentProps<typeof Badge>['variant']>

const TIER_VARIANT: Record<string, BadgeVariant> = {
  INVITED: 'success',
  PROMISING: 'warning',
  OTHER: 'secondary',
}

const PERSONA_LABELS: Record<string, string> = {
  oracle: 'The Oracle',
  hustler: 'The Hustler',
  architect: 'The Architect',
  hawk: 'The Hawk',
}

const METRIC_LABELS: Record<string, string> = {
  vision: 'Vision',
  traction: 'Traction',
  problem_solution: 'Problem/Solution',
  market: 'Market',
  team: 'Team',
}

const VALIDATION_VARIANT: Record<string, BadgeVariant> = {
  APPROVED: 'success',
  DECLINED: 'destructive',
  UNVALIDATED: 'secondary',
}

function getSignalBadgeVariant(strength: SignalStrength): BadgeVariant {
  const map: Record<SignalStrength, BadgeVariant> = {
    STRONG: 'success',
    MODERATE: 'warning',
    WEAK: 'destructive',
  }
  return map[strength]
}

function getGapBadgeVariant(strength: SignalStrength): BadgeVariant {
  const map: Record<SignalStrength, BadgeVariant> = {
    STRONG: 'destructive',
    MODERATE: 'warning',
    WEAK: 'success',
  }
  return map[strength]
}

function StrengthBadge({
  strength,
  type,
}: {
  strength: SignalStrength | null
  type: 'signal' | 'gap'
}) {
  if (!strength) return null
  const variant = type === 'signal' ? getSignalBadgeVariant(strength) : getGapBadgeVariant(strength)
  return (
    <Badge variant={variant} size="sm">
      {strength}
    </Badge>
  )
}

function MetadataCard({ prescreen }: { prescreen: Prescreen }) {
  return (
    <div className="space-y-4 rounded-lg border border-border bg-card p-4">
      <div className="grid grid-cols-2 gap-4 text-sm">
        <div>
          <span className="text-muted-foreground">Startup</span>
          <p>
            <Link
              to="/admin/startups/$id"
              params={{ id: prescreen.startupId }}
              className="text-[#f97316] hover:underline"
            >
              {prescreen.startupName ?? 'Unknown'}
            </Link>
          </p>
        </div>
        <div>
          <span className="text-muted-foreground">Founder</span>
          <p>
            {prescreen.founder ? (
              <Link
                to="/admin/users/$id"
                params={{ id: prescreen.founder.id }}
                className="text-[#f97316] hover:underline"
              >
                {prescreen.founder.name || prescreen.founder.email}
              </Link>
            ) : (
              '-'
            )}
          </p>
        </div>
        <div>
          <span className="text-muted-foreground">Investor</span>
          <p>
            {prescreen.investorId ? (
              <Link
                to="/admin/investors/$id"
                params={{ id: prescreen.investorId }}
                className="text-[#f97316] hover:underline"
              >
                {prescreen.investorName ?? 'Unknown'}
              </Link>
            ) : (
              '-'
            )}
          </p>
        </div>
        <div>
          <span className="text-muted-foreground">Pitch Deck ID</span>
          <p className="font-mono text-xs">{prescreen.pitchDeckId ?? '-'}</p>
        </div>
        <div>
          <span className="text-muted-foreground">Validation Status</span>
          <p>
            <Badge
              variant={VALIDATION_VARIANT[prescreen.validationStatus] ?? 'secondary'}
              size="sm"
            >
              {prescreen.validationStatus}
            </Badge>
          </p>
        </div>
        <div>
          <span className="text-muted-foreground">Tier</span>
          <p>
            {prescreen.evaluation?.tier ? (
              <Badge variant={TIER_VARIANT[prescreen.evaluation.tier] ?? 'secondary'} size="sm">
                {prescreen.evaluation.tier}
              </Badge>
            ) : (
              '-'
            )}
          </p>
        </div>
        <div>
          <span className="text-muted-foreground">Score</span>
          <p>{prescreen.evaluation?.score != null ? `${prescreen.evaluation.score}/100` : '-'}</p>
        </div>
      </div>
    </div>
  )
}

function SignalGapActionCards({
  signal,
  signalStrength,
  gap,
  gapStrength,
  action,
}: {
  signal: string | null
  signalStrength: SignalStrength | null
  gap: string | null
  gapStrength: SignalStrength | null
  action: string | null
}) {
  if (!signal && !gap && !action) return null

  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
      {signal && (
        <div className="rounded-md border-l-2 border-l-emerald-400/50 bg-emerald-400/5 p-3">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-sm font-medium">Signal</span>
            <StrengthBadge strength={signalStrength} type="signal" />
          </div>
          <MarkdownContent>{signal}</MarkdownContent>
        </div>
      )}
      {gap && (
        <div className="rounded-md border-l-2 border-l-red-400/50 bg-red-400/5 p-3">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-sm font-medium">Gap</span>
            <StrengthBadge strength={gapStrength} type="gap" />
          </div>
          <MarkdownContent>{gap}</MarkdownContent>
        </div>
      )}
      {action && (
        <div className="rounded-md border-l-2 border-l-amber-400/50 bg-amber-400/5 p-3">
          <span className="mb-2 block text-sm font-medium">Action</span>
          <MarkdownContent>{action}</MarkdownContent>
        </div>
      )}
    </div>
  )
}

function EvaluationSummary({ evaluation }: { evaluation: PrescreenEvaluation }) {
  return (
    <div className="space-y-4 rounded-lg border border-border bg-card p-4">
      <h3 className="text-sm font-medium">Evaluation Summary</h3>

      {evaluation.verdict && <MarkdownContent>{evaluation.verdict}</MarkdownContent>}

      <SignalGapActionCards
        signal={evaluation.signal}
        signalStrength={evaluation.signalStrength}
        gap={evaluation.gap}
        gapStrength={evaluation.gapStrength}
        action={evaluation.action}
      />
    </div>
  )
}

function PersonaCard({
  persona,
  evaluations,
}: {
  persona: string
  evaluations: IndividualMetricEvaluation[]
}) {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div className="rounded-lg border border-border bg-card overflow-hidden">
        <CollapsibleTrigger className="flex w-full items-center justify-between p-4 hover:bg-card/80 transition-colors">
          <div className="flex items-center gap-3">
            <span className="font-medium">{PERSONA_LABELS[persona] ?? persona}</span>
          </div>
          <ChevronDown
            className={cn(
              'size-4 text-muted-foreground transition-transform',
              isOpen && 'rotate-180'
            )}
          />
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="border-t border-border p-4 space-y-6">
            {evaluations
              .sort((a, b) => a.metric.localeCompare(b.metric))
              .map((evaluation) => (
                <div
                  key={evaluation.id}
                  className="border-b border-border pb-6 last:border-0 last:pb-0 space-y-4"
                >
                  <h4 className="text-sm font-medium">
                    {METRIC_LABELS[evaluation.metric] ?? evaluation.metric}
                  </h4>
                  <SignalGapActionCards
                    signal={evaluation.signal}
                    signalStrength={evaluation.signalStrength}
                    gap={evaluation.gap}
                    gapStrength={evaluation.gapStrength}
                    action={evaluation.action}
                  />
                </div>
              ))}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  )
}

function PersonaAccordion({ evaluations }: { evaluations: IndividualMetricEvaluation[] }) {
  const groupedByPersona = evaluations.reduce(
    (acc, evaluation) => {
      const { persona } = evaluation
      if (!acc[persona]) {
        acc[persona] = []
      }
      acc[persona].push(evaluation)
      return acc
    },
    {} as Record<string, IndividualMetricEvaluation[]>
  )

  return (
    <div className="space-y-4">
      {Object.entries(groupedByPersona).map(([persona, evals]) => (
        <PersonaCard key={persona} persona={persona} evaluations={evals} />
      ))}
    </div>
  )
}

export function PrescreenDetail({ prescreen }: { prescreen: Prescreen }) {
  const { evaluation } = prescreen

  return (
    <div className="space-y-6 py-6">
      <MetadataCard prescreen={prescreen} />

      {evaluation ? (
        <>
          <EvaluationSummary evaluation={evaluation} />
          {evaluation.prescreenIndividualMetricEvaluations.length > 0 && (
            <PersonaAccordion evaluations={evaluation.prescreenIndividualMetricEvaluations} />
          )}
        </>
      ) : (
        <div className="rounded-lg border border-border bg-card p-6 text-center">
          <p className="text-sm text-muted-foreground">
            No evaluation available.{' '}
            {prescreen.status === 'PENDING' && 'The pre-screen is still pending.'}
            {prescreen.status === 'PROCESSING' && 'The pre-screen is currently being processed.'}
            {prescreen.status === 'FAILED' && 'The pre-screen evaluation failed.'}
          </p>
        </div>
      )}
    </div>
  )
}
